/** Fail-closed admission for Pilot's Foundation platform OAuth clients. */

import type { PlatformOperatorMembership } from "./platform-operator-membership";
import {
  isUuidOrCompact,
  toDashedUuid,
} from "@garagem/foundation-shared/lib/utils/uuid";

export const PILOT_PLATFORM_AUDIENCE =
  "https://app.garagem.ai/api/pilot" as const;

/**
 * Browser OAuth client for a human Pilot operator.
 * The machine client (`pilot-service`) never goes through this consent flow.
 */
export const PILOT_OPERATOR_OAUTH_CLIENT_ID = "pilot-web" as const;
export const FOUNDATION_USERINFO_AUDIENCE =
  "https://app.garagem.ai/api/auth/oauth2/userinfo" as const;

export const PILOT_OPERATOR_SCOPES = [
  "openid",
  "profile",
  "email",
  "offline_access",
  "pilot:support:read",
  "pilot:support:triage",
  "pilot:alerts:read",
  "pilot:investigations:read",
  "pilot:investigations:run",
  "pilot:agents:dispatch",
  "pilot:responses:send",
] as const;

/**
 * Rollout-gated operator scopes. Keep these out of the default Pilot consent
 * and provisioning arrays until the existing OAuth client/resource policy is
 * explicitly expanded; old sessions must remain usable in the meantime.
 */
export const PILOT_OPERATOR_OPTIONAL_SCOPES = [
  "pilot:operator:read",
  "pilot:offboarding:read",
  "pilot:impersonation:issue",
  "pilot:intelligence:read",
  "pilot:intelligence:body",
  "pilot:intelligence:manage",
  "pilot:meetings:read",
  "pilot:meetings:manage",
  "pilot:email:read",
  "pilot:email:manage",
  // Pilot's own operator settings (YOLO merging). No Foundation capability
  // requires it; the Pilot dashboard checks it on the operator's token.
  "pilot:settings:manage",
] as const;

export const PILOT_SERVICE_SCOPES = [
  "pilot:investigations:execute",
  "pilot:evidence:write",
  "pilot:responses:deliver",
  "pilot:releases:enrich",
  "pilot:releases:evidence",
  "pilot:support:digest",
  "pilot:support:release_delivery",
  "pilot:support:origin_delivery",
] as const;

export type PilotOperatorScope =
  | (typeof PILOT_OPERATOR_SCOPES)[number]
  | (typeof PILOT_OPERATOR_OPTIONAL_SCOPES)[number];
export type PilotServiceScope = (typeof PILOT_SERVICE_SCOPES)[number];
export type PilotPlatformScope = PilotOperatorScope | PilotServiceScope;
export type PlatformOAuthClientKind = "operator" | "service";

export type PlatformOAuthClientRecord = {
  clientId: string;
  disabled: boolean | null;
  metadata: unknown;
  resourceIdentifiers: readonly string[];
};

export type PlatformOAuthResourceRecord = {
  identifier: string;
  disabled: boolean | null;
  allowedScopes: readonly string[] | null;
  metadata: unknown;
};

/** Raw payload returned by Better Auth's verifyBearerToken. */
export type BetterAuthPlatformTokenClaims = {
  client_id?: unknown;
  sub?: unknown;
  aud?: unknown;
  scope?: unknown;
  exp?: unknown;
  iat?: unknown;
  nbf?: unknown;
};

export type PlatformOAuthRequestContext = {
  tenantHeaderPresent: boolean;
  organizationHeaderPresent: boolean;
};

export type OperatorEligibility = PlatformOperatorMembership;

export type PlatformOAuthAdmission =
  | {
      kind: "operator";
      clientId: string;
      userId: string;
      organizationId: string;
      tenantId: string;
      scopes: readonly PilotOperatorScope[];
      audience: typeof PILOT_PLATFORM_AUDIENCE;
    }
  | {
      kind: "service";
      clientId: string;
      service: "pilot";
      scopes: readonly PilotServiceScope[];
      audience: typeof PILOT_PLATFORM_AUDIENCE;
    };

export type PlatformOAuthAdmissionFailureCode =
  | "INVALID_TOKEN_CLAIMS"
  | "UNKNOWN_CLIENT"
  | "CLIENT_MISMATCH"
  | "CLIENT_DISABLED"
  | "INVALID_CLIENT_METADATA"
  | "RESOURCE_NOT_ALLOWED"
  | "RESOURCE_DISABLED"
  | "INVALID_RESOURCE_METADATA"
  | "WRONG_AUDIENCE"
  | "TENANT_CONTEXT_FORBIDDEN"
  | "INVALID_REQUIRED_SCOPES"
  | "SCOPE_NOT_ALLOWED"
  | "RESOURCE_SCOPE_NOT_ALLOWED"
  | "REQUIRED_SCOPE_MISSING"
  | "OPERATOR_NOT_ELIGIBLE"
  | "INVALID_SERVICE_SUBJECT";

export type PlatformOAuthAdmissionResult =
  | { ok: true; admission: PlatformOAuthAdmission }
  | { ok: false; code: PlatformOAuthAdmissionFailureCode };

const PILOT_CAPABILITY_SCOPE_REQUIREMENTS = {
  "pilot.email.aliases": ["pilot:email:read"],
  "pilot.email.list": ["pilot:email:read"],
  "pilot.email.get": ["pilot:email:read"],
  "pilot.email.alias_create": ["pilot:email:manage"],
  "pilot.email.alias_disable": ["pilot:email:manage"],
  "pilot.meetings.connections": ["pilot:meetings:read"],
  "pilot.meetings.calendars": ["pilot:meetings:read"],
  "pilot.meetings.agenda": ["pilot:meetings:read"],
  "pilot.meetings.proposal_get": ["pilot:meetings:read"],
  "pilot.meetings.connect_issue": ["pilot:meetings:manage"],
  "pilot.meetings.select_calendar": ["pilot:meetings:manage"],
  "pilot.meetings.disconnect": ["pilot:meetings:manage"],
  "pilot.meetings.sync_next": ["pilot:meetings:manage"],
  "pilot.meetings.proposal_create": ["pilot:meetings:manage"],
  "pilot.meetings.proposal_update": ["pilot:meetings:manage"],
  "pilot.meetings.execute_confirmed": ["pilot:meetings:manage"],
  "pilot.meetings.recording_start": ["pilot:meetings:manage"],
  "pilot.meetings.recording_stop": ["pilot:meetings:manage"],
  "pilot.meetings.recording_assign_customer": ["pilot:meetings:manage"],
  "pilot.meetings.customer_history": ["pilot:meetings:read"],
  "pilot.meetings.recording_transcript": ["pilot:meetings:read"],
  "admin.organization.search": ["pilot:operator:read"],
  "admin.organization.get": ["pilot:operator:read"],
  "admin.organization.get_owner": ["pilot:operator:read"],
  "admin.organization.list_members": ["pilot:operator:read"],
  "admin.tenant_offboarding.pending.list": ["pilot:offboarding:read"],
  "admin.tenant_offboarding.runs.list": ["pilot:offboarding:read"],
  "admin.organization.impersonation_link.issue": ["pilot:impersonation:issue"],
  "admin.intelligence.signal.list": ["pilot:intelligence:read"],
  "admin.intelligence.signal.body": ["pilot:intelligence:body"],
  "admin.intelligence.signal.promote": ["pilot:intelligence:manage"],
  "admin.intelligence.signal.dismiss": ["pilot:intelligence:manage"],
  "admin.intelligence.product_need.list": ["pilot:intelligence:read"],
  "admin.intelligence.product_need.get": ["pilot:intelligence:read"],
  "admin.intelligence.product_need.set_status": ["pilot:intelligence:manage"],
  "pilot.alerts.list": ["pilot:alerts:read"],
  "pilot.support.cases.list": ["pilot:support:read"],
  "pilot.support.cases.team_pm_snapshot": ["pilot:support:digest"],
  "pilot.support.cases.delivery_list": ["pilot:support:read"],
  "pilot.support.cases.delivery_requeue": ["pilot:support:triage"],
  "pilot.support.cases.suggest_attachment": ["pilot:support:read"],
  "pilot.support.cases.grouping_quality": ["pilot:support:read"],
  "pilot.support.cases.review_attachment": ["pilot:support:triage"],
  "pilot.support.cases.digest_changes": ["pilot:support:digest"],
  "pilot.support.cases.slack_card": ["pilot:support:digest"],
  "pilot.support.cases.digest_ack": ["pilot:support:digest"],
  "pilot.support.cases.origin_bind": ["pilot:support:origin_delivery"],
  "pilot.support.cases.source_confirm": ["pilot:support:triage"],
  "pilot.support.cases.origin_transitions": ["pilot:support:origin_delivery"],
  "pilot.support.cases.origin_prepare": ["pilot:support:origin_delivery"],
  "pilot.support.cases.origin_ack": ["pilot:support:origin_delivery"],
  "pilot.support.cases.origin_quiesce": ["pilot:support:origin_delivery"],
  "pilot.support.cases.origin_review": ["pilot:support:triage"],
  "pilot.support.cases.origin_deliveries": ["pilot:support:read"],
  "pilot.support.whatsapp.groups": ["pilot:support:read"],
  "pilot.support.whatsapp.conversation": ["pilot:support:read"],
  "pilot.support.whatsapp.reply": ["pilot:support:triage"],
  "pilot.support.whatsapp.reply_status": ["pilot:support:read"],
  "pilot.support.whatsapp.resolve_reply": ["pilot:support:triage"],
  "pilot.customers.board.list": ["pilot:support:read"],
  "pilot.customers.board.set_stage": ["pilot:support:triage"],
  "pilot.customers.board.update": ["pilot:support:triage"],
  "pilot.customers.workbench.get": ["pilot:support:read"],
  "pilot.customers.workbench.open_case": ["pilot:support:triage"],
  "pilot.customers.pending.list": ["pilot:support:read"],
  "pilot.customer_work.list": ["pilot:support:read"],
  "pilot.customer_work.demand_create": ["pilot:support:triage"],
  "pilot.customer_work.demand_list": ["pilot:support:read"],
  "pilot.customer_work.demand_get": ["pilot:support:read"],
  "pilot.customer_work.demand_update": ["pilot:support:triage"],
  "pilot.customer_work.demand_link_case": ["pilot:support:triage"],
  "pilot.customer_work.release_link": ["pilot:support:triage"],
  "pilot.customer_work.release_read": ["pilot:support:read"],
  "pilot.customer_work.release_followthrough": ["pilot:support:read"],
  "pilot.customer_work.release_merge": ["pilot:releases:evidence"],
  "pilot.customer_work.release_evidence_plan": ["pilot:releases:evidence"],
  "pilot.customer_work.release_deployment": ["pilot:releases:evidence"],
  "pilot.customer_work.release_ancestry": ["pilot:releases:evidence"],
  "pilot.customer_work.release_reconcile": ["pilot:support:release_delivery"],
  "pilot.customer_work.release_reconcile_next": [
    "pilot:support:release_delivery",
  ],
  "pilot.customer_work.release_claim": ["pilot:support:release_delivery"],
  "pilot.customer_work.release_ack": ["pilot:support:release_delivery"],
  "pilot.customer_work.release_block": ["pilot:support:release_delivery"],
  "pilot.customer_work.release_review_blocked": ["pilot:support:triage"],
  "pilot.formulas.catalog": ["pilot:investigations:read"],
  "pilot.formulas.list": ["pilot:investigations:read"],
  "pilot.formulas.preview": ["pilot:investigations:read"],
  "pilot.formulas.review_policy": ["pilot:investigations:read"],
  "pilot.formulas.activate_policy": ["pilot:investigations:run"],
  "pilot.formulas.start": ["pilot:investigations:run"],
  "pilot.formulas.cancel": ["pilot:investigations:run"],
  "pilot.formulas.set_pause": ["pilot:investigations:run"],
  "pilot.formulas.resume_observation": ["pilot:investigations:run"],
  "pilot.formulas.get": ["pilot:investigations:read"],
  "pilot.formulas.decide_retry": ["pilot:investigations:run"],
  "pilot.formulas.diagnose_certificate": ["pilot:investigations:read"],
  "pilot.formulas.apply_certificate_correction": ["pilot:investigations:run"],
  "pilot.incidents.list": ["pilot:alerts:read", "pilot:investigations:read"],
  "pilot.incidents.get": ["pilot:alerts:read", "pilot:investigations:read"],
  "pilot.incidents.board": ["pilot:alerts:read", "pilot:investigations:read"],
  "pilot.incidents.overview": [
    "pilot:alerts:read",
    "pilot:investigations:read",
  ],
  "pilot.incidents.customers": [
    "pilot:alerts:read",
    "pilot:investigations:read",
  ],
  "pilot.incidents.fix": ["pilot:investigations:run"],
  "pilot.incidents.link_pr": ["pilot:investigations:run"],
  "pilot.incidents.fix_targets": ["pilot:releases:evidence"],
  "pilot.incidents.fix_evidence": ["pilot:releases:evidence"],
  "pilot.incidents.associate_merged_prs": ["pilot:releases:evidence"],
  "pilot.incidents.delivery_list": [
    "pilot:alerts:read",
    "pilot:investigations:read",
  ],
  "pilot.incidents.delivery_requeue": ["pilot:investigations:run"],
  "pilot.incidents.delivery_reconcile_active": ["pilot:investigations:run"],
  "pilot.incidents.approve": ["pilot:investigations:run"],
  "pilot.incidents.cancel": ["pilot:investigations:run"],
  "pilot.incidents.retry": ["pilot:investigations:run"],
  "pilot.incidents.escalate": ["pilot:investigations:run"],
  "pilot.incidents.triage_axiom": ["pilot:investigations:execute"],
  "pilot.incidents.triage_card": ["pilot:investigations:execute"],
  "pilot.support.cases.get": ["pilot:support:read"],
  "pilot.support.cases.assign": ["pilot:support:triage"],
  "pilot.support.cases.assign_organization": ["pilot:support:triage"],
  "pilot.support.cases.set_status": ["pilot:support:triage"],
  "pilot.support.lifecycle.preview": ["pilot:support:read"],
  "pilot.support.lifecycle.recheck": ["pilot:support:triage"],
  "pilot.support.cases.set_hold": ["pilot:support:triage"],
  "pilot.support.cases.set_priority": ["pilot:support:triage"],
  "pilot.support.cases.comment": ["pilot:support:triage"],
  "pilot.support.cases.split_signal": ["pilot:support:triage"],
  "pilot.support.cases.request_evidence": ["pilot:support:triage"],
  "pilot.support.cases.request_investigation": [
    "pilot:support:triage",
    "pilot:investigations:run",
  ],
  "pilot.investigations.execute": ["pilot:investigations:execute"],
  "pilot.investigations.lookup_code_evidence": ["pilot:investigations:execute"],
  "pilot.investigations.search_code_evidence": ["pilot:investigations:read"],
  "pilot.evidence.write": ["pilot:evidence:write"],
  "pilot.responses.deliver": ["pilot:responses:deliver"],
  "pilot.releases.summarize": ["pilot:releases:enrich"],
} as const satisfies Record<string, readonly PilotPlatformScope[]>;

type NormalizedClaims = {
  clientId: string;
  subject: string;
  audiences: readonly string[];
  scopes: readonly string[];
};

type PilotClientMetadata = {
  contextMode: "platform";
  platformApplication: "pilot";
  principalType: PlatformOAuthClientKind;
  audience: typeof PILOT_PLATFORM_AUDIENCE;
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isPilotPlatformClientMetadata(value: unknown): boolean {
  return (
    isObject(value) &&
    value.contextMode === "platform" &&
    value.platformApplication === "pilot"
  );
}

export function tokenTargetsPilotPlatform(
  raw: BetterAuthPlatformTokenClaims
): boolean {
  const audiences = uniqueStrings(raw.aud);
  return audiences?.includes(PILOT_PLATFORM_AUDIENCE) ?? false;
}

/** Unknown capabilities are deliberately unmapped and therefore denied. */
export function requiredPilotScopesForCapability(
  capabilityId: string
): readonly PilotPlatformScope[] {
  return (
    PILOT_CAPABILITY_SCOPE_REQUIREMENTS[
      capabilityId as keyof typeof PILOT_CAPABILITY_SCOPE_REQUIREMENTS
    ] ?? []
  );
}

function owns(value: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function uniqueStrings(value: unknown): string[] | null {
  const values = typeof value === "string" ? [value] : value;
  if (!Array.isArray(values) || values.length === 0) return null;
  if (values.some((item) => typeof item !== "string" || !item.trim())) {
    return null;
  }
  return Array.from(new Set(values.map((item) => item.trim())));
}

function isNumericDate(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) > 0;
}

/**
 * Better Auth's verifier currently permits its clock-tolerance window after
 * `exp`. Pilot's platform boundary is deliberately stricter: an access token
 * is dead at its signed expiry second, and malformed temporal claims deny.
 */
export function hasValidPlatformOAuthTemporalClaims(
  raw: BetterAuthPlatformTokenClaims,
  nowEpochSeconds = Math.floor(Date.now() / 1_000)
): boolean {
  if (!isNumericDate(raw.exp) || !isNumericDate(raw.iat)) return false;
  if (raw.exp <= nowEpochSeconds || raw.iat > nowEpochSeconds) return false;
  if (raw.iat >= raw.exp) return false;
  if (raw.nbf === undefined) return true;
  return isNumericDate(raw.nbf) && raw.nbf <= nowEpochSeconds;
}

/** The only boundary that translates Better Auth's raw JWT claim names. */
export function normalizeBetterAuthPlatformClaims(
  raw: BetterAuthPlatformTokenClaims
): NormalizedClaims | null {
  if (typeof raw.client_id !== "string" || !raw.client_id.trim()) return null;
  if (typeof raw.sub !== "string" || !raw.sub.trim()) return null;
  const audiences = uniqueStrings(raw.aud);
  if (!audiences) return null;
  if (typeof raw.scope !== "string") return null;
  const scopes = Array.from(
    new Set(
      raw.scope
        .split(/\s+/)
        .map((scope) => scope.trim())
        .filter(Boolean)
    )
  );
  if (scopes.length === 0) return null;
  return {
    clientId: raw.client_id.trim(),
    subject: raw.sub.trim(),
    audiences,
    scopes,
  };
}

export function platformOAuthRequestContextFromRequest(
  request: Pick<Request, "headers">
): PlatformOAuthRequestContext {
  return {
    tenantHeaderPresent: request.headers.has("x-garagem-tenant-id"),
    organizationHeaderPresent: request.headers.has("x-garagem-organization-id"),
  };
}

function parseClientMetadata(value: unknown): PilotClientMetadata | null {
  if (!isObject(value)) return null;
  if (value.contextMode !== "platform") return null;
  if (value.platformApplication !== "pilot") return null;
  if (value.audience !== PILOT_PLATFORM_AUDIENCE) return null;
  if (value.principalType !== "operator" && value.principalType !== "service") {
    return null;
  }
  if (owns(value, "tenantId") || owns(value, "organizationId")) return null;
  return value as PilotClientMetadata;
}

function isPilotPlatformResource(value: unknown): boolean {
  return (
    isObject(value) &&
    value.contextMode === "platform" &&
    value.platformApplication === "pilot" &&
    !owns(value, "tenantId") &&
    !owns(value, "organizationId")
  );
}

function audiencesAreAllowed(
  audiences: readonly string[],
  scopes: readonly string[]
): boolean {
  const allowed = new Set<string>([
    PILOT_PLATFORM_AUDIENCE,
    FOUNDATION_USERINFO_AUDIENCE,
  ]);
  const onlyKnownAudiences =
    audiences.includes(PILOT_PLATFORM_AUDIENCE) &&
    audiences.every((audience) => allowed.has(audience));
  const userinfoIsOidcOnly =
    !audiences.includes(FOUNDATION_USERINFO_AUDIENCE) ||
    scopes.includes("openid");
  return onlyKnownAudiences && userinfoIsOidcOnly;
}

function scopeFailure(args: {
  kind: PlatformOAuthClientKind;
  granted: readonly string[];
  required: readonly PilotPlatformScope[];
  resourceAllowed: readonly string[] | null;
}): PlatformOAuthAdmissionFailureCode | null {
  const permitted = new Set<string>(
    args.kind === "operator"
      ? [...PILOT_OPERATOR_SCOPES, ...PILOT_OPERATOR_OPTIONAL_SCOPES]
      : PILOT_SERVICE_SCOPES
  );
  if (
    args.required.length === 0 ||
    args.required.some((s) => !permitted.has(s))
  ) {
    return "INVALID_REQUIRED_SCOPES";
  }
  if (args.granted.some((scope) => !permitted.has(scope))) {
    return "SCOPE_NOT_ALLOWED";
  }
  if (
    !args.resourceAllowed ||
    args.granted.some((scope) => !args.resourceAllowed?.includes(scope))
  ) {
    return "RESOURCE_SCOPE_NOT_ALLOWED";
  }
  if (args.required.some((scope) => !args.granted.includes(scope))) {
    return "REQUIRED_SCOPE_MISSING";
  }
  return null;
}

/** Better Auth treats `disabled: null` as enabled; only literal true denies. */
function isDisabled(value: boolean | null): boolean {
  return value === true;
}

function canonicalOperatorUserId(value: string): string {
  return isUuidOrCompact(value) ? toDashedUuid(value).toLowerCase() : value;
}

/** Shared live policy boundary for token admission and session-bound browser
 * handoffs. Candidate detection is insufficient: both metadata records and
 * exact client/resource identities must satisfy the same full policy. */
export function validatePilotPlatformOAuthPolicy(input: {
  client: PlatformOAuthClientRecord | null;
  resource: PlatformOAuthResourceRecord | null;
}):
  | {
      ok: true;
      metadata: PilotClientMetadata;
      resource: PlatformOAuthResourceRecord;
    }
  | { ok: false; code: PlatformOAuthAdmissionFailureCode } {
  if (!input.client) return { ok: false, code: "UNKNOWN_CLIENT" };
  if (isDisabled(input.client.disabled)) {
    return { ok: false, code: "CLIENT_DISABLED" };
  }

  const metadata = parseClientMetadata(input.client.metadata);
  if (!metadata) return { ok: false, code: "INVALID_CLIENT_METADATA" };
  if (
    metadata.principalType === "operator" &&
    input.client.clientId !== PILOT_OPERATOR_OAUTH_CLIENT_ID
  ) {
    return { ok: false, code: "CLIENT_MISMATCH" };
  }
  if (!input.client.resourceIdentifiers.includes(PILOT_PLATFORM_AUDIENCE)) {
    return { ok: false, code: "RESOURCE_NOT_ALLOWED" };
  }
  if (
    !input.resource ||
    input.resource.identifier !== PILOT_PLATFORM_AUDIENCE
  ) {
    return { ok: false, code: "RESOURCE_NOT_ALLOWED" };
  }
  if (isDisabled(input.resource.disabled)) {
    return { ok: false, code: "RESOURCE_DISABLED" };
  }
  if (!isPilotPlatformResource(input.resource.metadata)) {
    return { ok: false, code: "INVALID_RESOURCE_METADATA" };
  }
  return { ok: true, metadata, resource: input.resource };
}

export function admitPilotPlatformOAuth(input: {
  rawClaims: BetterAuthPlatformTokenClaims;
  client: PlatformOAuthClientRecord | null;
  resource: PlatformOAuthResourceRecord | null;
  requestContext: PlatformOAuthRequestContext;
  requiredScopes: readonly PilotPlatformScope[];
  operatorEligibility: OperatorEligibility | null;
  nowEpochSeconds?: number;
}): PlatformOAuthAdmissionResult {
  if (
    !hasValidPlatformOAuthTemporalClaims(input.rawClaims, input.nowEpochSeconds)
  ) {
    return { ok: false, code: "INVALID_TOKEN_CLAIMS" };
  }
  const claims = normalizeBetterAuthPlatformClaims(input.rawClaims);
  if (!claims) return { ok: false, code: "INVALID_TOKEN_CLAIMS" };
  if (!input.client) return { ok: false, code: "UNKNOWN_CLIENT" };
  if (claims.clientId !== input.client.clientId) {
    return { ok: false, code: "CLIENT_MISMATCH" };
  }
  const policy = validatePilotPlatformOAuthPolicy(input);
  if (!policy.ok) return policy;
  const metadata = policy.metadata;
  if (!audiencesAreAllowed(claims.audiences, claims.scopes)) {
    return { ok: false, code: "WRONG_AUDIENCE" };
  }
  if (
    input.requestContext.tenantHeaderPresent ||
    input.requestContext.organizationHeaderPresent
  ) {
    return { ok: false, code: "TENANT_CONTEXT_FORBIDDEN" };
  }

  const scopesError = scopeFailure({
    kind: metadata.principalType,
    granted: claims.scopes,
    required: input.requiredScopes,
    resourceAllowed: policy.resource.allowedScopes,
  });
  if (scopesError) return { ok: false, code: scopesError };

  if (metadata.principalType === "operator") {
    const subject = canonicalOperatorUserId(claims.subject);
    if (
      !input.operatorEligibility ||
      canonicalOperatorUserId(input.operatorEligibility.userId) !== subject
    ) {
      return { ok: false, code: "OPERATOR_NOT_ELIGIBLE" };
    }
    return {
      ok: true,
      admission: {
        kind: "operator",
        clientId: input.client.clientId,
        // Carry the canonical token subject only after the authoritative
        // membership row proves the compact/dashed UUID candidate.
        userId: subject,
        organizationId: input.operatorEligibility.organizationId,
        tenantId: input.operatorEligibility.tenantId,
        scopes: claims.scopes as PilotOperatorScope[],
        audience: PILOT_PLATFORM_AUDIENCE,
      },
    };
  }

  if (claims.subject !== claims.clientId) {
    return { ok: false, code: "INVALID_SERVICE_SUBJECT" };
  }
  return {
    ok: true,
    admission: {
      kind: "service",
      clientId: input.client.clientId,
      service: "pilot",
      scopes: claims.scopes as PilotServiceScope[],
      audience: PILOT_PLATFORM_AUDIENCE,
    },
  };
}
